'use client';
import React, { useState } from 'react';
import { CourseList } from '@/app/types/course';
import { useAuth } from '@/app/Components/AuthProvider';
import { useCourseLists } from '@/app/hooks/useCourseLists';
import CourseSearch from './CourseSearch';
import { TrashIcon, PencilIcon, ShareIcon, PlusIcon, BookOpenIcon } from '@heroicons/react/24/outline';
import { CheckIcon, XMarkIcon } from '@heroicons/react/24/solid';
import CourseName from '@/app/Components/CourseName';
import { formatBaseIdDisplay } from '@/app/lib/utils';
import { CourseLink } from '@/app/Components/CoursePeek/CourseLink';
import Modal from '@/app/Components/Modal';
import { PageLoading } from '@/app/Components/LoadingBubbles';
import { useSearchParams } from 'next/navigation';
import { useUiPreferences } from '@/app/Components/UiPreferencesProvider';
import { SEMESTER_OPTIONS } from '@/app/lib/semesters';

interface CourseListsProps {
    onCourseListSelect?: (courseList: CourseList) => void;
    pendingCourseId?: string | null;
    onCourseAdded?: () => void;
}

export default function CourseLists({ pendingCourseId, onCourseAdded }: CourseListsProps) {
    const requestedSemester = useSearchParams().get('semester');
    const { activeSemester } = useUiPreferences();
    const semester = SEMESTER_OPTIONS.find(({ id }) => id === requestedSemester)?.id ?? activeSemester;
    const [newListName, setNewListName] = useState('');
    const [newListDescription, setNewListDescription] = useState('');
    const { user } = useAuth();
    const [editingListId, setEditingListId] = useState<string | null>(null);
    const [editCourses, setEditCourses] = useState<any[]>([]);
    const [showModal, setShowModal] = useState(false);
    const [modalMessage, setModalMessage] = useState('');
    const [addingToListId, setAddingToListId] = useState<string | null>(null);

    // Use the new hook for course lists management
    const {
        courseLists,
        isLoading,
        error,
        createCourseList,
        updateCourseList,
        deleteCourseList
    } = useCourseLists();

    const handleCreateList = async () => {
        if (!newListName.trim()) return;
        try {
            // Create course list with empty classes array
            await createCourseList({
                name: newListName.trim(),
                description: newListDescription.trim() || null,
                classes: []
            });

            // Clear form
            setNewListName('');
            setNewListDescription('');

            // Show success message
            setModalMessage('Course list created successfully!');
            setShowModal(true);
        } catch (err) {
            console.error('Error creating course list:', err);
            // Error is already handled by the hook
        }
    };

    const handleViewList = async (listId: string) => {
        window.location.href = `/course-list?${new URLSearchParams({ id: listId, semester })}`;
    };

    const handleShare = async (listId: string) => {
        const shareUrl = `${window.location.origin}/course-list?${new URLSearchParams({ id: listId, semester })}`;
        try {
            await navigator.clipboard.writeText(shareUrl);
            setModalMessage('Share link copied to clipboard!');
            setShowModal(true);
        } catch (err) {
            console.error('Error copying share link:', err);
            // Show error in modal instead
            setModalMessage('Failed to copy shareable link');
            setShowModal(true);
        }
    };

    const handleDelete = async (listId: string) => {
        if (!confirm('Are you sure you want to delete this course list?')) return;
        try {
            await deleteCourseList(parseInt(listId));
            // The hook will automatically update the list, so no need to re-fetch or filter here
        } catch (err) {
            console.error('Error deleting course list:', err);
            // Error is already handled by the hook
        }
    };

    const handleEditList = (list: CourseList) => {
        setEditingListId(list.id);
        // Convert the stored course IDs back to course objects for editing
        // For now, we'll just store the IDs as simple objects
        const courseObjects = (list.classes || []).map(courseId => ({
            id: courseId,
            name: courseId // This should be replaced with actual course name lookup
        }));
        setEditCourses(courseObjects);
    };

    const handleSaveEdit = async (listId: string) => {
        try {
            // Save course IDs in the format: ["01:198:111", "01:640:111", etc.]
            const coursesToSave = editCourses.map((course) => course.id);
            await updateCourseList(parseInt(listId), {
                classes: coursesToSave,
                user_generated: user?.id
            });
            setEditingListId(null);
            setEditCourses([]);
            // The hook will automatically update the list, so no need to re-fetch or filter here
        } catch (err) {
            console.error('Error updating course list:', err);
            // Error is already handled by the hook
        }
    };

    const handleCancelEdit = () => {
        setEditingListId(null);
        setEditCourses([]);
    };

    const handleAddCourseToEdit = (course: any) => {
        // Prevent duplicates and add the base course ID (e.g., "01:198:111")
        if (!editCourses.some((c) => c.id === course.id)) {
            setEditCourses([...editCourses, {
                id: course.id,
                name: course.name
            }]);
        }
    };

    const handleRemoveCourseFromEdit = (courseId: string) => {
        setEditCourses(editCourses.filter((c) => c.id !== courseId));
    };

    const handleAddPendingCourse = async (list: CourseList) => {
        if (!pendingCourseId) return;
        const courseCode = pendingCourseId.replace(/\D/g, '').replace(/^(\d{2})(\d{3})(\d{3})$/, '$1:$2:$3');
        if (!courseCode) return;
        if ((list.classes || []).some((entry) => entry.replace(/\D/g, '') === courseCode.replace(/\D/g, ''))) {
            setModalMessage(`${courseCode} is already in ${list.name}.`);
            setShowModal(true);
            onCourseAdded?.();
            return;
        }
        try {
            setAddingToListId(list.id);
            await updateCourseList(parseInt(list.id), { classes: [...(list.classes || []), courseCode], user_generated: user?.id });
            setModalMessage(`${courseCode} was added to ${list.name}.`);
            setShowModal(true);
            onCourseAdded?.();
        } finally {
            setAddingToListId(null);
        }
    };

    if (isLoading) {
        return (
            <PageLoading
                title="Loading course lists"
                description="Gathering your saved planning collections."
            />
        );
    }

    if (error) {
        return (
            <div className="bg-red-500/10 border border-red-500/20 rounded-control p-4 text-red-600 dark:text-red-300">
                {error}
            </div>
        );
    }

    return (
        <>
            <div className="space-y-8">
                {pendingCourseId ? <div role="status" className="rounded-card border border-[var(--ss-border)] bg-[var(--ss-surface-subtle)] p-4 text-sm text-[var(--ss-text)]"><strong>Choose a course list</strong><p className="mt-1 text-[var(--ss-text-muted)]">Add {pendingCourseId.replace(/\D/g, '').replace(/^(\d{2})(\d{3})(\d{3})$/, '$1:$2:$3')} to one of your saved lists.</p></div> : null}
                {/* Create New List Section */}
                <div className="rounded-feature border border-slate-200 bg-white p-6 shadow-e1 dark:border-white/10 dark:bg-slate-900/80">
                    <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                        <div>
                            <h2 className="flex items-center text-xl font-bold text-slate-900 dark:text-white">
                                <PlusIcon className="mr-2 h-6 w-6 text-red-600" />
                                Create New Course List
                            </h2>
                            <p className="mt-2 max-w-2xl text-sm text-slate-600 dark:text-slate-400">
                                Build a reusable shortlist for degree requirements, backups, themed electives, or advisor recommendations.
                            </p>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            <span className="rounded-full bg-red-50 px-3 py-1 text-xs font-semibold text-red-700 dark:bg-red-900/20 dark:text-red-300">
                                Personal planning
                            </span>
                            <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                                Shareable detail pages
                            </span>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-end">
                        <div className="space-y-2">
                            <label className="label">List Name</label>
                            <input
                                type="text"
                                value={newListName}
                                onChange={(e) => setNewListName(e.target.value)}
                                placeholder="e.g., CS Core Requirements"
                                className="input" />
                        </div>
                        <div className="space-y-2">
                            <label className="label">Description (Optional)</label>
                            <input
                                type="text"
                                value={newListDescription}
                                onChange={(e) => setNewListDescription(e.target.value)}
                                placeholder="Brief description of this course list"
                                className="input" />
                        </div>
                    </div>
                    <div className="mt-6 flex justify-end">
                        <button
                            type="button"
                            onClick={handleCreateList}
                            disabled={!newListName.trim()}
                            className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Create List
                        </button>
                    </div>
                </div>

                {/* Course Lists */}
                <div>
                    <div className="mb-6 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
                        <div>
                            <h2 className="flex items-center text-2xl font-bold text-slate-900 dark:text-white">
                                <BookOpenIcon className="mr-2 h-6 w-6 text-red-600" />
                                My Course Lists
                            </h2>
                            <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
                                Open, edit, share, or prune the course collections you reuse across planning workflows.
                            </p>
                        </div>
                        <div className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                            {courseLists.length} saved lists
                        </div>
                    </div>

                    {courseLists && courseLists.length === 0 ? (
                        <div className="rounded-feature border border-dashed border-slate-300 bg-white px-6 py-14 text-center shadow-e1 dark:border-slate-700 dark:bg-slate-900/70">
                            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-card bg-slate-100 dark:bg-slate-800">
                                <BookOpenIcon className="h-8 w-8 text-slate-400 dark:text-slate-500" />
                            </div>
                            <p className="text-xl font-semibold text-slate-900 dark:text-white mb-2">No course lists yet</p>
                            <p className="mx-auto max-w-md text-sm text-slate-500 dark:text-slate-400">
                                Start with a major checklist, an electives stack, or a backup plan for registration week.
                            </p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 gap-6">
                            {courseLists.map((list) => (
                                <div key={list.id} className="card group relative overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-e2">
                                    <div className="absolute top-0 left-0 w-1 h-full bg-red-600 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                                    <div className="flex flex-col md:flex-row justify-between items-start gap-6">
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-3 mb-2">
                                                <h3 className="text-xl font-bold text-slate-900 dark:text-white truncate">{list.name}</h3>
                                                <span className="badge badge-red">
                                                    {list.classes?.length ?? 0} courses
                                                </span>
                                            </div>
                                            {list.description && (
                                                <p className="text-slate-600 dark:text-slate-400 mb-4">{list.description}</p>
                                            )}

                                            {/* Course Preview Chips */}
                                            {list.classes && list.classes.length > 0 && !editingListId && (
                                                <div className="flex flex-wrap gap-2 mt-4">
                                                    {list.classes.slice(0, 6).map((courseId) => (
                                                        <span key={courseId} className="px-2.5 py-1 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded text-sm font-medium border border-slate-200 dark:border-slate-600">
                                                            <CourseLink courseId={courseId} context={{ label: 'In your course list', facts: [{ label: 'List', value: list.name }, { label: 'Courses in list', value: String(list.classes.length) }] }}><span className="mr-1.5 font-mono text-xs opacity-70">{formatBaseIdDisplay(courseId)}</span><CourseName code={courseId} /></CourseLink>
                                                        </span>
                                                    ))}
                                                    {list.classes.length > 6 && (
                                                        <span className="px-2.5 py-1 text-slate-500 dark:text-slate-400 text-sm font-medium">
                                                            +{list.classes.length - 6} more
                                                        </span>
                                                    )}
                                                </div>
                                            )}
                                        </div>

                                        {/* Action Buttons */}
                                        <div className="flex items-center gap-2 self-start md:self-center shrink-0">
                                            {editingListId === list.id ? (
                                                <div className="flex gap-2">
                                                    <button
                                                        type="button"
                                                        onClick={() => handleSaveEdit(list.id)}
                                                        className="p-2 bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300 rounded-control hover:bg-green-200 dark:hover:bg-green-900/50 transition-colors"
                                                        title="Save"
                                                        aria-label={`Save changes to ${list.name}`}
                                                    >
                                                        <CheckIcon className="h-5 w-5" />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={handleCancelEdit}
                                                        className="p-2 bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300 rounded-control hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
                                                        title="Cancel"
                                                        aria-label={`Cancel editing ${list.name}`}
                                                    >
                                                        <XMarkIcon className="h-5 w-5" />
                                                    </button>
                                                </div>
                                            ) : (
                                                <div className="flex gap-2 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                                                    {pendingCourseId ? <button type="button" onClick={() => handleAddPendingCourse(list)} disabled={addingToListId === list.id} className="btn-primary text-sm !py-1.5 disabled:opacity-60">{addingToListId === list.id ? 'Adding…' : 'Add course'}</button> : null}
                                                    <button
                                                        type="button"
                                                        onClick={() => handleEditList(list)}
                                                        className="p-2 text-slate-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-control transition-colors"
                                                        title="Edit"
                                                        aria-label={`Edit ${list.name}`}
                                                    >
                                                        <PencilIcon className="h-5 w-5" />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleShare(list.id)}
                                                        className="p-2 text-slate-500 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-control transition-colors"
                                                        title="Share"
                                                        aria-label={`Share ${list.name}`}
                                                    >
                                                        <ShareIcon className="h-5 w-5" />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleDelete(list.id)}
                                                        className="p-2 text-slate-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-control transition-colors"
                                                        title="Delete"
                                                        aria-label={`Delete ${list.name}`}
                                                    >
                                                        <TrashIcon className="h-5 w-5" />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleViewList(list.id)}
                                                        className="btn-secondary text-sm !py-1.5 ml-2"
                                                    >
                                                        View Details
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Edit Mode Content */}
                                    {editingListId === list.id && (
                                        <div className="mt-6 pt-6 border-t border-slate-200 dark:border-slate-700">
                                            <div className="space-y-4">
                                                <CourseSearch
                                                    onSelectCourse={handleAddCourseToEdit}
                                                    selectedCourses={editCourses}
                                                    onRemoveCourse={handleRemoveCourseFromEdit}
                                                    onAddCoreBlock={() => { }}
                                                    selectedCoreBlocks={[]} />
                                                <div>
                                                    <h4 className="text-sm font-semibold text-slate-900 dark:text-white mb-3">Courses in List:</h4>
                                                    <div className="flex flex-wrap gap-2">
                                                        {editCourses.length === 0 ? (
                                                            <span className="text-slate-500 italic text-sm">No courses added yet</span>
                                                        ) : (
                                                            editCourses.map((course) => (
                                                                <span key={course.id} className="flex items-center bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-200 border border-red-100 dark:border-red-800 px-3 py-1.5 rounded-full text-sm font-medium gap-2">
                                                                    <CourseName code={course.id} />
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => handleRemoveCourseFromEdit(course.id)}
                                                                        className="text-red-400 hover:text-red-600 dark:hover:text-red-100"
                                                                        title="Remove"
                                                                        aria-label={`Remove ${course.id} from ${list.name}`}
                                                                    >
                                                                        <XMarkIcon className="h-4 w-4" />
                                                                    </button>
                                                                </span>
                                                            ))
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
            <Modal
                isOpen={showModal}
                onClose={() => setShowModal(false)}
                title={modalMessage}
                size="sm"
            >
                <div className="text-center py-4">
                    <button
                        type="button"
                        onClick={() => setShowModal(false)}
                        className="btn-primary w-full justify-center"
                    >
                        OK
                    </button>
                </div>
            </Modal></>
    );
}

export function CourseListsComponent(props: CourseListsProps) {
    return <CourseLists {...props} />;
}

export { CourseLists };
